/**
 * Tenants whose GROAS script is limited to specific first path segments.
 * The key is the tenant-config id from the `[tenant]` route, not publisherId.
 * Tenants omitted from this map stay unrestricted.
 */
export const GROAS_PATH_ALLOWLIST: ReadonlyMap<
  string,
  ReadonlySet<string>
> = new Map([['learnhowtobecome.org', new Set(['campaign'])]]);

/**
 * Whether this tenant only loads GROAS on allowlisted paths.
 *
 * @param {string | null | undefined} tenant - Tenant id from the route.
 * @returns {boolean} True when the tenant has a path allowlist.
 */
export const isGroasPathRestricted = (
  tenant: string | null | undefined,
): tenant is string => Boolean(tenant && GROAS_PATH_ALLOWLIST.has(tenant));

/**
 * Whether this tenant and pathname may load the GROAS content script.
 * Allowlisted tenants match the first page segment after an optional tenant
 * prefix. Every other tenant is unrestricted. A missing tenant fails closed.
 *
 * @param {string | undefined} tenant - Tenant id from the route.
 * @param {string | null} pathname - App Router pathname.
 * @returns {boolean} True when the page is in scope for GROAS.
 */
export const shouldLoadGroasForPath = (
  tenant: string | undefined,
  pathname: string | null,
): boolean => {
  if (!tenant) {
    return false;
  }

  const allowedSegments = GROAS_PATH_ALLOWLIST.get(tenant);
  if (!allowedSegments) {
    return true;
  }

  const segments = pathname?.split('/').filter(Boolean) ?? [];
  const tenantIndex = segments.indexOf(tenant);
  const pageSegments =
    tenantIndex >= 0 ? segments.slice(tenantIndex + 1) : segments;

  const firstSegment = pageSegments[0];
  return firstSegment !== undefined && allowedSegments.has(firstSegment);
};
